import React, { useCallback, useEffect, useState } from 'react';
import { Send, Download, FileCheck, FileText, Loader, FileDown, History, AlertTriangle } from 'lucide-react';
import { Button, Spinner } from 'react-bootstrap';
import { getApiUrl } from '../../../../../utils/ApiUtils';
import axios from '../../../../../services/AxiosInstance';
import { toast } from 'sonner';

const SubmissionStep = ({
    vatPeriod,
    companyId,
    vatPeriodId,
    operationInProgress,
    isPreviewMode,
    markStepComplete,
    completionStatus,
    onReportGenerated
}) => {
    const [generating, setGenerating] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [generatingPdf, setGeneratingPdf] = useState(false);
    const [versions, setVersions] = useState([]);

    // The filing history (G40). A period regenerated AFTER it was submitted is
    // a rättelse, and R-COR-1 makes that a FULL replacement declaration rather
    // than a delta — worth saying plainly, because the word "rapport" hides it.
    const loadVersions = useCallback(async () => {
        try {
            const response = await axios.get(
                getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/report-versions`),
                { withCredentials: true }
            );
            if (response.data.success) setVersions(response.data.versions || []);
        } catch (error) {
            setVersions([]);
        }
    }, [companyId, vatPeriodId]);

    useEffect(() => { loadVersions(); }, [loadVersions, vatPeriod?.vat_report_generated_at]);

    const isCorrection = versions.some((version) => Number(version.is_correction) === 1);

    const handleGeneratePdf = async () => {
        setGeneratingPdf(true);
        try {
            // The endpoint streams the PDF, the same way "Ladda ner XML-fil"
            // below fetches the eSKD file. It used to return a blob URL that the
            // browser opened directly, which failed with Azure's
            // PublicAccessNotPermitted — the storage account is private.
            const response = await axios.post(
                getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/generate-report-pdf`),
                {},
                { withCredentials: true, responseType: 'blob' }
            );

            const url = window.URL.createObjectURL(
                new Blob([response.data], { type: 'application/pdf' })
            );
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `momsdeklaration-${vatPeriod?.start_date || ''}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            // An error response arrives as a Blob too, so the JSON message has to
            // be read out of it rather than off error.response.data.
            let message = 'Kunde inte skapa PDF:en';
            const data = error.response?.data;
            if (data instanceof Blob) {
                try {
                    const parsed = JSON.parse(await data.text());
                    if (parsed.message) message = parsed.message;
                } catch (e) { /* not JSON — keep the default */ }
            } else if (data?.message) {
                message = data.message;
            }
            toast.error(message);
        } finally {
            setGeneratingPdf(false);
        }
    };

    const handleGenerateReport = async () => {
        setGenerating(true);

        try {
            // Intention only: identity, period and amounts come from the server.
            const response = await axios.post(
                getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/generate-report`),
                {},
                { withCredentials: true }
            );

            if (response.data.success) {
                toast.success("Momsrapport genererad framgångsrikt");
                if (onReportGenerated) onReportGenerated();
            } else {
                toast.error("Kunde inte generera momsrapporten");
            }
        } catch (error) {
            console.error('Error generating VAT report:', error);
            toast.error(error.response?.data?.message || "Ett fel uppstod vid generering av rapporten");
        } finally {
            setGenerating(false);
        }
    };

    const handleDownloadReport = async () => {
        setDownloading(true);

        try {
            const response = await axios.get(
                getApiUrl(`${companyId}/download/vat-report/${vatPeriodId}`),
                {
                    withCredentials: true,
                    responseType: 'blob' // Important: tells axios to handle binary data
                }
            );

            // Create a blob URL and trigger download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', vatPeriod.file_name || 'momsrapport.xml');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            toast.success("Momsrapport nedladdad");
        } catch (error) {
            console.error('Error downloading VAT report:', error);
            toast.error(error.response?.data?.message || "Ett fel uppstod vid nedladdning av rapporten");
        } finally {
            setDownloading(false);
        }
    };

    const handleConfirmSubmission = async () => {
        setConfirming(true);

        try {
            await markStepComplete('submission');
            toast.success("Inlämning till Skatteverket bekräftad");
        } catch (error) {
            console.error('Error confirming submission:', error);
            toast.error("Kunde inte bekräfta inlämningen");
        } finally {
            setConfirming(false);
        }
    };

    return (
        <div className="step-container">
            <div className="step-header">
                <div className="d-flex align-items-center">
                    <div
                        className="rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{
                            width: '48px',
                            height: '48px',
                            backgroundColor: 'var(--primary)',
                            color: 'white'
                        }}
                    >
                        <Send size={24} />
                    </div>
                    <div>
                        <h3 className="mb-1">Skicka Momsrapport</h3>
                        <p className="text-muted mb-0">Generera och skicka rapporten till Skatteverket</p>
                    </div>
                </div>
            </div>

            <div className="step-content">
                <style>{`
                    .submission-file {
                        border: 1px solid var(--rgba-primary-2, #e5e7eb);
                        border-radius: 10px;
                        padding: 1rem 1.15rem;
                        margin-bottom: 1.25rem;
                    }
                    .submission-file-name {
                        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
                        font-size: 0.8rem;
                        color: #6b7280;
                        word-break: break-all;
                    }
                    .submission-steps {
                        counter-reset: submission-step;
                        list-style: none;
                        padding-left: 0;
                        margin: 0 0 1.15rem 0;
                    }
                    .submission-steps li {
                        counter-increment: submission-step;
                        position: relative;
                        padding-left: 2rem;
                        margin-bottom: 0.6rem;
                        line-height: 1.45;
                    }
                    .submission-confirmed {
                        border: 1px solid var(--rgba-primary-3, #cfe3d3);
                        background: var(--rgba-primary-1, #f4f8f5);
                        border-radius: 10px;
                        padding: 1rem 1.15rem;
                    }
                    .submission-confirmed-title {
                        font-weight: 600;
                        color: var(--primary);
                    }
                    .submission-confirmed-note {
                        color: #6b7280;
                        font-size: 0.82rem;
                        line-height: 1.45;
                        margin-top: 0.35rem;
                    }
                    .submission-steps li::before {
                        content: counter(submission-step);
                        position: absolute;
                        left: 0;
                        top: 0.05rem;
                        width: 1.4rem;
                        height: 1.4rem;
                        border-radius: 50%;
                        background: var(--primary);
                        color: #fff;
                        font-size: 0.72rem;
                        font-weight: 600;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }
                `}</style>

                <p className="mb-4 text-muted">
                    LedgerLoop skapar filen. Du laddar upp den hos Skatteverket och bekräftar
                    här när den är inlämnad — vi får aldrig något svar därifrån.
                </p>

                {!vatPeriod?.file_name ? (
                    <Button
                        variant="primary"
                        onClick={handleGenerateReport}
                        disabled={generating || operationInProgress || isPreviewMode || vatPeriod?.state === 'closed'}
                        className="d-inline-flex align-items-center"
                    >
                        {generating ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Genererar rapport...
                            </>
                        ) : (
                            <>
                                <FileText size={16} className="me-2" />
                                Generera momsrapport
                            </>
                        )}
                    </Button>
                ) : (
                    <div>
                      <div className="submission-file">
                        <div className="d-flex align-items-center mb-2">
                            <FileText size={16} className="me-2" style={{ color: 'var(--primary)' }} />
                            <div>
                                <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>
                                    Momsdeklarationen är genererad
                                </div>
                                <div className="submission-file-name">{vatPeriod.file_name}</div>
                            </div>
                        </div>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <Button
                                variant="primary"
                                onClick={handleDownloadReport}
                                disabled={downloading || operationInProgress || isPreviewMode}
                                className="d-inline-flex align-items-center"
                            >
                                {downloading ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Laddar ner...
                                    </>
                                ) : (
                                    <>
                                        <Download size={16} className="me-2" />
                                        Ladda ner XML-fil
                                    </>
                                )}
                            </Button>

                            {/* G22 — the PDF is rendered from the same frozen
                                snapshot as the XML, so a closed period's PDF
                                shows what was filed, not what the ledger says
                                today. */}
                            <Button
                                variant="outline-secondary"
                                onClick={handleGeneratePdf}
                                disabled={generatingPdf || operationInProgress || isPreviewMode}
                                className="d-inline-flex align-items-center"
                            >
                                {generatingPdf ? (
                                    <Spinner animation="border" size="sm" />
                                ) : (
                                    <>
                                        <FileDown size={14} className="me-1" />
                                        Ladda ner PDF
                                    </>
                                )}
                            </Button>

                            {!completionStatus?.submission_completed && vatPeriod?.state !== 'closed' && (
                                <Button
                                    variant="link"
                                    size="sm"
                                    onClick={handleGenerateReport}
                                    disabled={generating || operationInProgress || isPreviewMode}
                                    className="d-inline-flex align-items-center text-muted px-1"
                                    style={{ textDecoration: 'none' }}
                                >
                                    {generating ? (
                                        <Spinner animation="border" size="sm" />
                                    ) : (
                                        <>
                                            <FileText size={14} className="me-1" />
                                            Generera om
                                        </>
                                    )}
                                </Button>
                            )}
                        </div>

                      </div>

                        {isCorrection && (
                            <div className="alert alert-warning d-flex align-items-start mb-3">
                                <AlertTriangle size={18} className="me-2 mt-1 flex-shrink-0" />
                                <div>
                                    <div className="fw-semibold">Det här är en rättelse</div>
                                    Deklarationen togs fram efter att perioden redan lämnats in. En
                                    rättelse ersätter <strong>hela</strong> deklarationen för perioden —
                                    den är inte en skillnad mot det som lämnats tidigare. Lämna den under
                                    “Inlämnade momsdeklarationer → Rätta momsdeklaration” hos Skatteverket.{' '}
                                    <a href="https://www.skatteverket.se/foretag/moms/deklareramoms/rattaenmomsdeklaration.4.3684199413c956649b552c4.html"
                                        target="_blank" rel="noopener noreferrer">Skatteverket</a>
                                </div>
                            </div>
                        )}

                        {versions.length > 1 && (
                            <div className="mb-3">
                                <div className="text-muted small d-flex align-items-center gap-1 mb-1">
                                    <History size={14} /> Tidigare versioner sparas och raderas aldrig
                                </div>
                                <ul className="text-muted small mb-0 ps-3">
                                    {versions.map((version) => (
                                        <li key={version.version_id}>
                                            Version {version.version_no} — {version.generated_at}
                                            {Number(version.is_correction) === 1 ? ' (rättelse)' : ''}
                                            {version.superseded_at ? ` · ersatt ${version.superseded_at}` : ' · aktuell'}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {!completionStatus?.submission_completed ? (
                            <div className="mb-3 p-3" style={{ backgroundColor: 'var(--rgba-primary-1)', border: '1px solid var(--rgba-primary-3)', borderRadius: '10px' }}>
                                <p className="mb-3 fw-semibold">Så lämnar du in</p>
                                <ol className="submission-steps">
                                    <li>Ladda ner XML-filen ovan.</li>
                                    <li>
                                        Logga in hos{' '}
                                        <a href="https://www.skatteverket.se" target="_blank" rel="noopener noreferrer">
                                            Skatteverket
                                        </a>{' '}
                                        och ladda upp filen.
                                    </li>
                                    <li>Kom tillbaka hit och bekräfta att den är inlämnad.</li>
                                </ol>
                                <Button
                                    onClick={handleConfirmSubmission}
                                    disabled={confirming || operationInProgress || isPreviewMode}
                                    className="d-inline-flex align-items-center border-0"
                                    style={{ background: 'var(--primary)' }}
                                >
                                    {confirming ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Bekräftar...
                                        </>
                                    ) : (
                                        <>
                                            <FileCheck size={16} className="me-2" />
                                            Bekräfta inlämning till Skatteverket
                                        </>
                                    )}
                                </Button>
                            </div>
                        ) : (
                            <div className="submission-confirmed">
                                {/* Not `alert alert-success`: in this stylesheet
                                    that renders bold #68e365 text on pale green —
                                    low contrast and off the app's palette. Matches
                                    the file card and numbered flow above instead. */}
                                <div className="d-flex align-items-start">
                                    <FileCheck
                                        size={18}
                                        className="me-2 flex-shrink-0"
                                        style={{ color: 'var(--primary)', marginTop: '0.1rem' }}
                                    />
                                    <span className="submission-confirmed-title">
                                        {isCorrection
                                            ? 'Rättelse bekräftad som inlämnad'
                                            : 'Bekräftad som inlämnad till Skatteverket'}
                                    </span>
                                </div>
                                {/* The status vocabulary is deliberately precise:
                                    LedgerLoop GENERATES a file, the USER confirms
                                    they submitted it, and a later regeneration is a
                                    CORRECTION. We never learn anything from
                                    Skatteverket, and claiming otherwise would be a
                                    receipt we cannot back. */}
                                <div className="submission-confirmed-note">
                                    Bekräftelsen är din egen — LedgerLoop får ingen kvittens från
                                    Skatteverket och kan inte se om filen tagits emot.
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SubmissionStep;
